import { ChevronRightOutlined, WarningAmberOutlined } from '@mui/icons-material'
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { SaveAndResetButtons } from 'components/Common/DialogActionButtons/SaveAndResetButtons'
import { DefinitiveMoneyPerQuantityField } from 'components/DefinitiveInputs/DefinitiveMoneyPerQuantityField'
import { DefinitiveInputValidators } from 'components/DefinitiveInputs/Validators'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { ProjectArticleController } from 'controllers/Project/ProjectArticleController'
import { useAppController } from 'customHooks/useAppController'
import { SurchargeAndDiscountSmallTable } from 'features/BillOfMaterials/ProjectViews/DashboardView/ProjectOverview/components/SurchargeAndDiscountSmallTable'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { MoneyString, SumMoney } from 'model/Money'
import { QuantityString } from 'model/Quantity'
import { TimeString } from 'model/TimeString'
import React, { useCallback, useEffect, useState, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MoneyPerQuantityDto,
  PriceSummaryDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import { getPriceSummaryKey } from 'store/Project/ProjectStateUtils'
import { makeStyles } from 'tss-react/mui'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { HomePriceButtonOutlined } from './ProjectPriceSummaries/HomePriceButtonOutlined'
import { useProjectPriceSummaryDetails } from './ProjectPriceSummaries/useProjectPriceSummaryDetails'
import { priceSummariesGroupedSelector } from './ProjectTotals/ProjectTotals.selectors'

const getLoadingKey = (operation: string, priceSummary: PriceSummaryDto) => {
  return `${operation}-${priceSummary.workingStep.resource.id}-${priceSummary.workingStep.secondaryWorkingStep}`
}

const useStyles = makeStyles({ name: 'project-financial-details' })(
  (theme) => ({
    titleRow: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      cursor: 'pointer',
    },
    saveButtons: {
      display: 'flex',
      gap: theme.spacing(1),
      width: '100%',
      justifyContent: 'center',
    },
    rateInput: {
      display: 'flex',
      alignItems: 'baseline',
      '& span, p, .MuiInput-root': {
        fontSize: theme.typography.body2.fontSize,
      },
      '& input[name="money"]': {
        paddingTop: '0px',
        paddingBottom: '3px',
      },
      '& input[name="quantity"]': {
        paddingTop: '0px',
        paddingBottom: '0px',
      },
    },
    deleteCustomCostPrice: {
      // position: 'absolute',
    },
    rateColumn: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      flexDirection: 'column',
      // paddingBottom: '1rem',
      paddingBottom: theme.spacing(0.2),
      '& input': {
        padding: '2px !important',
      },
      '& .MuiSelect-select': {
        padding: '2px',
      },
      '& .MuiFormHelperText-root': {
        '& span': {
          fontSize: theme.typography.caption.fontSize,
        },
      },
    },
    tableContainer: {
      // overflowX: 'auto',
      // overflowY: 'visible', // Allow vertical content to flow out
      position: 'relative', // Ensure proper stacking context
    },
    tableRoot: {
      padding: theme.spacing(2),
      borderCollapse: 'separate', // Important for sticky to work properly
      borderSpacing: 0, // Ensure no gaps between cells
      '& > tbody > tr > td:first-child': {
        position: 'sticky',
        left: 0,
        zIndex: 1,
        backgroundColor: theme.palette.background.default,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '& > thead > tr > th': {
        position: 'sticky',
        top: '56px', // For proper sticky header positioning
        zIndex: 3,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& > thead > tr > th:first-child': {
        position: 'sticky',
        left: 0,
        zIndex: 4, // Highest z-index for corner cell (header + first column)
        backgroundColor: theme.palette.background.default,
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '& > tbody > tr > td:last-child': {
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '& > tbody > tr > td': {
        backgroundColor: theme.palette.background.paper,
      },
    },
  }),
)

type MaterialItemProps = {
  materialPriceSummary: PriceSummaryDto
  isEditable: boolean
}

type ResourceItemProps = {
  priceSummaries: PriceSummaryDto[]
  resourceId: string
  isEditable: boolean
}

const ProjectFinancialResourceRowItem = (props: {
  priceSummary: PriceSummaryDto
  resourceId: string
  isEditable: boolean
}) => {
  const {
    changedPriceSummary,
    handleChange,
    handleResetToDefaults,
    loading,
    handleSubmit,
    handleReset,
  } = useProjectPriceSummaryDetails(props.priceSummary)

  const [rateIsValid, setRateIsValid] = useState(false)

  const { classes } = useStyles()

  const localizedWorkingStep = useLocalizedWorkingStepLabel()
  return (
    <TableRow
      sx={{
        verticalAlign: 'middle',
        // backgroundColor: (t) => `${t.palette.action.hover} !important`,
      }}
    >
      <TableCell sx={{ paddingLeft: (t) => t.spacing(2) }}>
        <Typography variant="body2" sx={{ pl: 8 }}>
          {localizedWorkingStep(
            props.priceSummary.workingStep.secondaryWorkingStep,
          )}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <div>
          <Typography variant="caption" noWrap component="p">
            {TimeString(props.priceSummary.productionTime)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {MoneyString(props.priceSummary.productionTimeCosts)}
          </Typography>
        </div>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <form onSubmit={handleSubmit}>
          <div className={classes.rateColumn}>
            <DefinitiveMoneyPerQuantityField
              value={changedPriceSummary.rate}
              onChange={(moneyPerQuantity) => {
                handleChange('rate', moneyPerQuantity)
              }}
              disabled={!props.isEditable}
              disabledUnit={!props.isEditable}
              disabledValue={!props.isEditable}
              disableWarningsIcon
              rootElementClassName={classes.rateInput}
              onValidation={(errors) => {
                setRateIsValid(!Boolean(errors?.length))
              }}
              validation={(field, moneyPerQuantity) => {
                if (field === 'money') {
                  return DefinitiveInputValidators.MinValue(
                    moneyPerQuantity?.money?.value,
                    0.01,
                  )()
                }

                if (field === 'quantity') {
                  return DefinitiveInputValidators.MinValue(
                    moneyPerQuantity?.quantity?.value,
                    0.01,
                  )()
                }

                return []
              }}
            />
            <div className={classes.deleteCustomCostPrice}>
              <HomePriceButtonOutlined
                handleResetToDefaults={() => handleResetToDefaults('costPrice')}
                isLoading={
                  loading[
                    getLoadingKey('delete-cost-price', changedPriceSummary)
                  ]
                }
                isManuallySet={changedPriceSummary.isCostPriceManuallySet}
                hidden={!props.isEditable}
              />
            </div>
            {!isEqual(changedPriceSummary.rate, props.priceSummary.rate) &&
              rateIsValid && (
                <div className={classes.saveButtons}>
                  <SaveAndResetButtons
                    disabled={
                      loading[
                        `${props.priceSummary.workingStep.resource.id}_costprice`
                      ]
                    }
                    handleReset={handleReset}
                    saving={
                      loading[
                        `${props.priceSummary.workingStep.resource.id}_costprice`
                      ]
                    }
                  />
                </div>
              )}
          </div>
        </form>
      </TableCell>
      <TableCell>
        <div>
          <Typography variant="body2" sx={{ textAlign: 'right' }}>
            {MoneyString(changedPriceSummary.totalCostPrice)}
          </Typography>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          ></div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {/* <Typography noWrap variant="caption" color="textSecondary">
              additional costs
            </Typography> */}
            <Typography noWrap variant="caption" color="textSecondary">
              {MoneyString(changedPriceSummary.additionalCosts)}
            </Typography>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div style={{ display: 'flex', paddingRight: '3em' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '50%',
              paddingRight: '1rem',
            }}
          >
            <Typography variant="caption">
              {MoneyString(changedPriceSummary.surchargeValue)}
            </Typography>
            <Typography variant="caption">
              {QuantityString(changedPriceSummary.surchargeRatio)}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '50%',
            }}
          >
            <Typography variant="caption">
              {MoneyString(changedPriceSummary.discountValue)}
            </Typography>
            <Typography variant="caption">
              {QuantityString(changedPriceSummary.discountRatio)}
            </Typography>
          </div>
        </div>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {MoneyString(changedPriceSummary.totalSalesPrice)}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

const ProjectFinancialResourceRow = (props: ResourceItemProps) => {
  const { classes } = useStyles()
  const [open, setOpen] = useState(true)

  const hasHourlyRateIssue = useAppSelector(
    projectSelectors.projectHourlyRateIssueSelector(
      props.priceSummaries.map((x) => x.workingStep.primaryWorkingStep),
    ),
    isEqual,
  )

  const [pending, setTranstion] = useTransition()

  const handleOpen = (open) => {
    setTranstion(() => {
      setOpen(open)
    })
  }

  return (
    <>
      <TableRow
        sx={
          {
            // backgroundColor: (t) => `${t.palette.background.paper} !important`,
          }
        }
      >
        <TableCell
          colSpan={2}
          style={{
            paddingLeft: '1.5rem',
          }}
        >
          <div
            className={classes.titleRow}
            onClick={() => {
              handleOpen(!open)
            }}
          >
            <IconButton
              size="small"
              // onClick={() => handleOpen(!open)}
              disabled={pending}
              style={{
                width: '1.5em',
              }}
            >
              {pending ? (
                <CircularProgress size="14px"></CircularProgress>
              ) : (
                <ChevronRightOutlined
                  style={{
                    transform: `rotate(${open ? 90 : 0}deg)`,
                  }}
                  fontSize="small"
                />
              )}
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {props.priceSummaries[0].workingStep.resource.name}
            </Typography>
          </div>
        </TableCell>
        <TableCell colSpan={2} align="center">
          {hasHourlyRateIssue && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'baseline',
              }}
            >
              <WarningAmberOutlined fontSize="small" color="warning" />
              <LocalizedTypography
                color="warning"
                translationKey="errors:hourly-rate-not-set"
              />
            </div>
          )}
        </TableCell>
        <TableCell>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
            }}
          >
            <SurchargeAndDiscountSmallTable
              priceSummary={props.priceSummaries[0]}
              noHeaders
            />
          </div>
        </TableCell>
        <TableCell>
          <Typography variant="body2" align="right">
            {MoneyString(
              SumMoney(props.priceSummaries.map((x) => x.totalSalesPrice)),
            )}
          </Typography>
        </TableCell>
      </TableRow>
      {open &&
        props.priceSummaries.map((priceSummary) => {
          return (
            <ProjectFinancialResourceRowItem
              key={getPriceSummaryKey(priceSummary)}
              priceSummary={priceSummary}
              resourceId={props.resourceId}
              isEditable={props.isEditable}
            />
          )
        })}
    </>
  )
}

const ProjectFinancialMaterialRow = (props: MaterialItemProps) => {
  const { classes } = useStyles()

  const [rate, setRate] = React.useState<MoneyPerQuantityDto>(
    props.materialPriceSummary?.rate,
  )

  const isEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )

  useEffect(() => {
    setRate(props.materialPriceSummary.rate)
  }, [props.materialPriceSummary?.rate])

  const { controller, loading } = useAppController(() => {
    return new ProjectArticleController()
  })

  const handleDeleteCostPrice = async () => {
    try {
      await controller.DeleteArticleCostPrice(
        props.materialPriceSummary.materialId,
      )
    } catch (err) {
      ShowException('unable to reset price', err)
    }
  }

  const handleSaveCostPrice = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      try {
        if (!isEqual(rate, props.materialPriceSummary.rate)) {
          await controller.SaveArticleCostPrice(
            getPriceSummaryKey(props.materialPriceSummary),
            props.materialPriceSummary.materialId,
            rate,
          )
        }
      } catch (error) {
        ShowException('Error saving cost price', error)
      }
    },
    [controller, props.materialPriceSummary, rate],
  )

  const [rateIsValid, setRateIsValid] = useState(false)

  const handleReset = useCallback(() => {
    setRate(props.materialPriceSummary.rate)
  }, [props.materialPriceSummary?.rate])

  return (
    <>
      <TableRow>
        <TableCell>
          <div>
            <Typography variant="body2">
              {props.materialPriceSummary.description}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {props.materialPriceSummary.materialId}
            </Typography>
          </div>
        </TableCell>
        <TableCell>
          <div style={{ textAlign: 'right' }}>
            <Typography variant="body2" noWrap>
              {QuantityString(
                props.materialPriceSummary.staggeredTotalQuantity,
              )}
            </Typography>
            {props.materialPriceSummary.totalQuantity.value !==
              props.materialPriceSummary.weight.value && (
              <Typography variant="caption" color={'textSecondary'}>
                {QuantityString(props.materialPriceSummary.totalQuantity)}
              </Typography>
            )}
          </div>
        </TableCell>
        <TableCell>
          <form onSubmit={handleSaveCostPrice}>
            <div className={classes.rateColumn}>
              <DefinitiveMoneyPerQuantityField
                // label={t('project:rate-per-time-unit', 'rate per time unit')}
                value={rate}
                onChange={(moneyPerQuantity) => {
                  setRate(moneyPerQuantity)
                }}
                disabled={!props.isEditable}
                disabledUnit={!props.isEditable}
                disabledValue={!props.isEditable}
                rootElementClassName={classes.rateInput}
                onValidation={(errors) => {
                  setRateIsValid(!Boolean(errors?.length))
                }}
                disableWarningsIcon
                validation={(field, moneyPerQuantity) => {
                  if (field === 'money') {
                    return DefinitiveInputValidators.MinValue(
                      moneyPerQuantity?.money?.value,
                      0.01,
                    )()
                  }

                  if (field === 'quantity') {
                    return DefinitiveInputValidators.MinValue(
                      moneyPerQuantity?.quantity?.value,
                      0.01,
                    )()
                  }
                  return []
                }}
              />
              {props.materialPriceSummary.isCostPriceManuallySet && (
                <div className={classes.deleteCustomCostPrice}>
                  <HomePriceButtonOutlined
                    handleResetToDefaults={handleDeleteCostPrice}
                    isManuallySet={
                      props.materialPriceSummary.isCostPriceManuallySet
                    }
                    isLoading={
                      loading[
                        `delete-cost-price-${props.materialPriceSummary.materialId}`
                      ]
                    }
                    hidden={!isEditable}
                  />
                </div>
              )}
              {!isEqual(rate, props.materialPriceSummary.rate) &&
                rateIsValid && (
                  <div className={classes.saveButtons}>
                    <SaveAndResetButtons
                      disabled={
                        loading[
                          `${props.materialPriceSummary.materialId}_costprice`
                        ]
                      }
                      handleReset={handleReset}
                      saving={
                        loading[
                          `${props.materialPriceSummary.materialId}_costprice`
                        ]
                      }
                    />
                  </div>
                )}
            </div>
          </form>
        </TableCell>
        <TableCell>
          <div
            style={{
              textAlign: 'right',
            }}
          >
            {MoneyString(props.materialPriceSummary.totalCostPrice)}
          </div>
        </TableCell>
        <TableCell>
          <SurchargeAndDiscountSmallTable
            priceSummary={props.materialPriceSummary}
            noHeaders
          />
        </TableCell>
        <TableCell>
          <div
            style={{
              textAlign: 'right',
            }}
          >
            {MoneyString(props.materialPriceSummary.totalSalesPrice)}
          </div>
        </TableCell>
      </TableRow>
    </>
  )
}

type Props = {
  materialVisible?: boolean
  workingStepVisible?: boolean
  visibleWorkingSteps?: WorkingStepType[]
}

export const ProjectFinancialDetails2 = (props: Props) => {
  const { cx, classes } = useStyles()
  const { t } = useTranslation()
  const { projectId } = useOrganizationAndProjectContext()

  const groupedPriceSummaries = useAppSelector(
    priceSummariesGroupedSelector,
    isEqual,
  )

  const isEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )

  const [openedRows, setOpenedRows] = useState<Record<string, boolean>>({
    materials: true,
    resources: true,
  })

  const [materialPending, materialTransition] = useTransition()

  const [resourcesPending, resourcesTransition] = useTransition()

  const handleRowToggle = (rowKey: 'materials' | 'resources') => {
    const transition = () => {
      setOpenedRows((prevState) => ({
        ...prevState,
        [rowKey]: !prevState[rowKey],
      }))
    }

    if (rowKey === 'materials') {
      materialTransition(transition)
    } else {
      resourcesTransition(transition)
    }
  }

  return (
    <>
      {/* <JsonViewer data={groupedPriceSummaries} /> */}
      <TableContainer className={classes.tableContainer}>
        <Table
          size="small"
          className={cx('root-table', classes.tableRoot)}
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                style={{
                  minWidth: '200px',
                }}
              >
                <Typography variant="caption">
                  {t('common:description')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="caption">
                  {t('project:quantity')}
                </Typography>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  minWidth: '250px',
                }}
              >
                <Typography variant="caption">
                  {t('project:cost-price')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="caption" noWrap>
                  {t('project:total-cost-price')}
                </Typography>
              </TableCell>
              <TableCell>
                <div style={{ textAlign: 'center' }}>
                  <Typography variant="caption">
                    {t('project:surcharge-and-discount', 'surcharge/discount')}
                  </Typography>
                </div>
              </TableCell>
              <TableCell align="right">
                <Typography variant="caption" noWrap>
                  {t('project:sales-price')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.materialVisible && (
              <>
                <TableRow>
                  <TableCell
                    style={{
                      borderRight: 'none',
                    }}
                  >
                    <div
                      className={classes.titleRow}
                      onClick={() => {
                        handleRowToggle('materials')
                      }}
                    >
                      <IconButton
                        size="small"
                        style={{
                          width: '2em',
                          height: '2em',
                        }}
                      >
                        {materialPending ? (
                          <CircularProgress size="12px"></CircularProgress>
                        ) : (
                          <ChevronRightOutlined
                            style={{
                              transform: `rotate(${openedRows['materials'] ? 90 : 0}deg)`,
                            }}
                          />
                        )}
                      </IconButton>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t('project:materials')}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell colSpan={5} />
                </TableRow>
                {openedRows['materials'] &&
                  groupedPriceSummaries.materials.priceSummaries.map(
                    (material) => {
                      return (
                        <ProjectFinancialMaterialRow
                          key={material.materialId}
                          materialPriceSummary={material}
                          isEditable={isEditable}
                        />
                      )
                    },
                  )}
              </>
            )}
            <TableRow>
              <TableCell colSpan={1} style={{ borderRight: 'none' }}>
                <div
                  className={classes.titleRow}
                  onClick={() => {
                    handleRowToggle('resources')
                  }}
                >
                  <IconButton
                    size="small"
                    style={{
                      width: '2em',
                      height: '2em',
                    }}
                  >
                    {resourcesPending ? (
                      <CircularProgress size="14px"></CircularProgress>
                    ) : (
                      <ChevronRightOutlined
                        style={{
                          transform: `rotate(${openedRows['resources'] ? 90 : 0}deg)`,
                        }}
                      />
                    )}
                  </IconButton>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t('project:resources')}
                  </Typography>
                </div>
              </TableCell>
              <TableCell colSpan={5} />
            </TableRow>
            {props.workingStepVisible &&
              openedRows['resources'] &&
              Object.entries(groupedPriceSummaries.resources)
                .filter(([resourceId, priceSummaries]) => {
                  if (props.visibleWorkingSteps?.length) {
                    return priceSummaries.some((priceSummary) =>
                      props.visibleWorkingSteps?.includes(
                        priceSummary.workingStep.secondaryWorkingStep,
                      ),
                    )
                  }
                  return true
                })
                .map(([resourceId, priceSummaries]) => {
                  return (
                    <ProjectFinancialResourceRow
                      key={resourceId}
                      resourceId={resourceId}
                      priceSummaries={priceSummaries}
                      isEditable={isEditable}
                    />
                  )
                })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
